// apps/web/src/components/cadastros/cliente-detalhe-sheet.tsx — Fase 07, Plano 06 (OMIE-08, D-04, UI-SPEC).
import React, { useEffect, useState, useCallback } from 'react';
import {
  AlertTriangle,
  Ban,
  Building2,
  CheckCircle2,
  Clock,
  DollarSign,
  FileText,
  MapPin,
  Phone,
  RefreshCw,
  Slash,
} from 'lucide-react';
import type { ClienteEntity, ClienteFinanceiroResponse } from '@pieltec/contracts';
import {
  deveExibirFinanceiro,
  formatarDocumentoFiscal,
  formatarMoeda,
  formatarTimestampFinanceiro,
  type SessionWithPermissions,
} from '../../lib/financeiro-badge';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import {
  FormDialog,
  FormDialogContent,
  FormDialogHeader,
  FormDialogTitle,
} from '../ui/form-dialog';
import { Skeleton } from '../ui/skeleton';

export interface ClienteDetalheSheetProps {
  readonly cliente: ClienteEntity | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly session?: SessionWithPermissions | null;
}

export function ClienteDetalheSheet({
  cliente,
  open,
  onOpenChange,
  session,
}: ClienteDetalheSheetProps): React.JSX.Element {
  const exibeFinanceiro = deveExibirFinanceiro(session);
  const [dadosFinanceiros, setDadosFinanceiros] = useState<ClienteFinanceiroResponse | null>(null);
  const [loadingFinanceiro, setLoadingFinanceiro] = useState(false);
  const [erroFinanceiro, setErroFinanceiro] = useState<string | null>(null);

  const carregarFinanceiro = useCallback(async (clienteId: string) => {
    setLoadingFinanceiro(true);
    setErroFinanceiro(null);
    try {
      const res = await fetch(`/api/v1/cadastros/clientes/${clienteId}/financeiro`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      if (!res.ok) {
        throw new Error(`Erro ao carregar dados financeiros (${res.status})`);
      }

      const data = (await res.json()) as ClienteFinanceiroResponse;
      setDadosFinanceiros(data);
    } catch (err: unknown) {
      setErroFinanceiro(err instanceof Error ? err.message : 'Falha ao buscar dados financeiros');
    } finally {
      setLoadingFinanceiro(false);
    }
  }, []);

  useEffect(() => {
    if (open && cliente && exibeFinanceiro) {
      void carregarFinanceiro(cliente.id);
    } else {
      setDadosFinanceiros(null);
      setErroFinanceiro(null);
    }
  }, [open, cliente, exibeFinanceiro, carregarFinanceiro]);

  if (!cliente) {
    return <FormDialog open={open} onOpenChange={onOpenChange}><FormDialogContent aria-describedby={undefined}><FormDialogHeader><FormDialogTitle>Carregando cliente</FormDialogTitle></FormDialogHeader></FormDialogContent></FormDialog>;
  }

  const stamp = formatarTimestampFinanceiro(dadosFinanceiros?.financeiroSincronizadoAt);

  return (
    <FormDialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent aria-describedby={undefined}>
        {/* Cabeçalho Fixo */}
        <FormDialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs font-bold px-2 py-0.5 bg-slate-100 text-slate-800 rounded">
              {cliente.codigo}
            </span>
            {cliente.ativo ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" />
                Ativo
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]">
                <Slash className="w-3 h-3 mr-1 text-slate-400" />
                Inativo
              </Badge>
            )}
          </div>
          <FormDialogTitle className="text-base font-bold text-slate-900 leading-snug">
            {cliente.nome}
          </FormDialogTitle>
          {cliente.nomeFantasia && (
            <p className="text-xs text-slate-500 font-normal mt-0.5">
              {cliente.nomeFantasia}
            </p>
          )}
        </FormDialogHeader>

        {(session?.permissions ?? session?.user?.permissions)?.includes('producao:consultar') ? (
          <div className="px-6 pb-2">
            <Button asChild variant="outline"><a href={`/cadastros/pecas?clienteId=${encodeURIComponent(cliente.id)}&clienteNome=${encodeURIComponent(cliente.nome)}`}>Peças / Produtos deste cliente</a></Button>
          </div>
        ) : null}

        {/* Corpo com Scroll */}
        <ScrollArea className="min-h-0 flex-1 overflow-y-auto p-6 space-y-6">
          {/* Seção 1: Dados Cadastrais (Visível a todos) */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="py-3 px-4 border-b bg-slate-50/50">
              <CardTitle className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-slate-500" />
                Informações Cadastrais
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-slate-400 block text-[11px]">Código</span>
                  <span className="font-mono font-medium text-slate-800">{cliente.codigo}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[11px]">
                    {cliente.tipoPessoa ? `${cliente.tipoPessoa} — Documento` : 'CNPJ / CPF'}
                  </span>
                  <span className="font-mono font-medium text-slate-800">
                    {formatarDocumentoFiscal(cliente.documentoFiscal)}
                  </span>
                </div>
              </div>

              <div>
                <span className="text-slate-400 block text-[11px]">Razão Social</span>
                <span className="font-medium text-slate-800">{cliente.nome}</span>
              </div>

              {cliente.nomeFantasia && (
                <div>
                  <span className="text-slate-400 block text-[11px]">Nome Fantasia</span>
                  <span className="font-medium text-slate-800">{cliente.nomeFantasia}</span>
                </div>
              )}

              {/* Subseção: Contato */}
              {(cliente.contatoFiscal?.telefone1Numero ||
                cliente.contatoFiscal?.telefone2Numero ||
                cliente.contatoFiscal?.email ||
                cliente.contatoFiscal?.contato) && (
                <div className="pt-2 border-t border-slate-100 space-y-2">
                  <span className="text-[11px] font-semibold text-slate-700 flex items-center gap-1.5">
                    <Phone className="w-3 h-3 text-slate-500" />
                    Contato
                  </span>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    {cliente.contatoFiscal.telefone1Numero && (
                      <div>
                        <span className="text-slate-400 block text-[10px]">Telefone Principal</span>
                        <span className="font-mono text-slate-800">
                          {cliente.contatoFiscal.telefone1Ddd ? `(${cliente.contatoFiscal.telefone1Ddd}) ` : ''}
                          {cliente.contatoFiscal.telefone1Numero.length === 9
                            ? `${cliente.contatoFiscal.telefone1Numero.slice(0, 5)}-${cliente.contatoFiscal.telefone1Numero.slice(5)}`
                            : cliente.contatoFiscal.telefone1Numero.length === 8
                              ? `${cliente.contatoFiscal.telefone1Numero.slice(0, 4)}-${cliente.contatoFiscal.telefone1Numero.slice(4)}`
                              : cliente.contatoFiscal.telefone1Numero}
                        </span>
                      </div>
                    )}
                    {cliente.contatoFiscal.telefone2Numero && (
                      <div>
                        <span className="text-slate-400 block text-[10px]">2º Telefone / Celular</span>
                        <span className="font-mono text-slate-800">
                          {cliente.contatoFiscal.telefone2Ddd ? `(${cliente.contatoFiscal.telefone2Ddd}) ` : ''}
                          {cliente.contatoFiscal.telefone2Numero.length === 9
                            ? `${cliente.contatoFiscal.telefone2Numero.slice(0, 5)}-${cliente.contatoFiscal.telefone2Numero.slice(5)}`
                            : cliente.contatoFiscal.telefone2Numero.length === 8
                              ? `${cliente.contatoFiscal.telefone2Numero.slice(0, 4)}-${cliente.contatoFiscal.telefone2Numero.slice(4)}`
                              : cliente.contatoFiscal.telefone2Numero}
                        </span>
                      </div>
                    )}
                    {cliente.contatoFiscal.email && (
                      <div className="col-span-2">
                        <span className="text-slate-400 block text-[10px]">E-mail</span>
                        <span className="text-slate-800 break-all">{cliente.contatoFiscal.email}</span>
                      </div>
                    )}
                    {cliente.contatoFiscal.contato && (
                      <div className="col-span-2">
                        <span className="text-slate-400 block text-[10px]">Pessoa de Contato</span>
                        <span className="text-slate-800 font-medium">{cliente.contatoFiscal.contato}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Subseção: Endereço Completo */}
              {(cliente.enderecoFiscal?.endereco ||
                cliente.enderecoFiscal?.cidade ||
                cliente.enderecoFiscal?.cep) && (
                <div className="pt-2 border-t border-slate-100 space-y-2">
                  <span className="text-[11px] font-semibold text-slate-700 flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 text-slate-500" />
                    Endereço
                  </span>
                  <div className="text-[11px] text-slate-800 space-y-0.5">
                    {cliente.enderecoFiscal.endereco && (
                      <div>
                        {cliente.enderecoFiscal.endereco}
                        {cliente.enderecoFiscal.enderecoNumero ? `, ${cliente.enderecoFiscal.enderecoNumero}` : ''}
                        {cliente.enderecoFiscal.complemento ? ` (${cliente.enderecoFiscal.complemento})` : ''}
                      </div>
                    )}
                    {(cliente.enderecoFiscal.bairro ||
                      cliente.enderecoFiscal.cidade ||
                      cliente.enderecoFiscal.estado ||
                      cliente.enderecoFiscal.cep) && (
                      <div className="text-slate-500">
                        {[
                          cliente.enderecoFiscal.bairro,
                          [cliente.enderecoFiscal.cidade, cliente.enderecoFiscal.estado].filter(Boolean).join(' - '),
                          cliente.enderecoFiscal.cep ? `CEP ${cliente.enderecoFiscal.cep}` : null,
                        ]
                          .filter(Boolean)
                          .join(' • ')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Subseção: Dados Fiscais / Classificação */}
              {(cliente.dadosFiscaisComplementares?.inscricaoEstadual ||
                cliente.dadosFiscaisComplementares?.inscricaoMunicipal ||
                cliente.dadosFiscaisComplementares?.optanteSimplesNacional !== undefined) && (
                <div className="pt-2 border-t border-slate-100 space-y-2">
                  <span className="text-[11px] font-semibold text-slate-700 flex items-center gap-1.5">
                    <FileText className="w-3 h-3 text-slate-500" />
                    Classificação Fiscal
                  </span>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    {cliente.dadosFiscaisComplementares.inscricaoEstadual && (
                      <div>
                        <span className="text-slate-400 block text-[10px]">Inscrição Estadual</span>
                        <span className="font-mono text-slate-800">
                          {cliente.dadosFiscaisComplementares.inscricaoEstadual}
                        </span>
                      </div>
                    )}
                    {cliente.dadosFiscaisComplementares.inscricaoMunicipal && (
                      <div>
                        <span className="text-slate-400 block text-[10px]">Inscrição Municipal</span>
                        <span className="font-mono text-slate-800">
                          {cliente.dadosFiscaisComplementares.inscricaoMunicipal}
                        </span>
                      </div>
                    )}
                    {cliente.dadosFiscaisComplementares.optanteSimplesNacional !== undefined &&
                      cliente.dadosFiscaisComplementares.optanteSimplesNacional !== null && (
                        <div>
                          <span className="text-slate-400 block text-[10px]">Simples Nacional</span>
                          <span className="text-slate-800 font-medium">
                            {cliente.dadosFiscaisComplementares.optanteSimplesNacional ? 'Optante' : 'Não Optante'}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Subseção: Observações */}
              {cliente.dadosFiscaisComplementares?.observacao && (
                <div className="pt-2 border-t border-slate-100 space-y-1">
                  <span className="text-[11px] font-semibold text-slate-700 block">Observações</span>
                  <p className="text-[11px] text-slate-600 whitespace-pre-wrap bg-slate-50 p-2 rounded-md border border-slate-100">
                    {cliente.dadosFiscaisComplementares.observacao}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Seção 2: Camadas Financeiras (DOM Omitido sem financeiro:consultar — D-04) */}
          {exibeFinanceiro && (
            <div className="space-y-4 pt-2">
              {/* Carimbo de Freshness */}
              <div className="flex items-center justify-between text-xs px-1">
                <div className="flex items-center gap-1.5 text-slate-500">
                  {stamp.sincronizado ? (
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                  ) : null}
                  <span className="text-[11px]">{stamp.texto}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void carregarFinanceiro(cliente.id)}
                  disabled={loadingFinanceiro}
                  className="h-6 px-2 text-[11px] text-slate-600 hover:text-slate-900"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${loadingFinanceiro ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>

              {loadingFinanceiro ? (
                <div className="space-y-3">
                  <Skeleton className="h-28 w-full rounded-lg" />
                  <Skeleton className="h-28 w-full rounded-lg" />
                  <Skeleton className="h-40 w-full rounded-lg" />
                </div>
              ) : erroFinanceiro ? (
                <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs space-y-2">
                  <p className="font-medium">Não foi possível carregar os dados financeiros.</p>
                  <p className="text-[11px] text-rose-600">{erroFinanceiro}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void carregarFinanceiro(cliente.id)}
                    className="h-7 text-xs bg-white hover:bg-rose-100/50"
                  >
                    Tentar novamente
                  </Button>
                </div>
              ) : dadosFinanceiros ? (
                <div className="space-y-4">
                  {/* 3b: Situação de Crédito na Omie */}
                  <Card className="bg-white border-slate-200">
                    <CardHeader className="py-3 px-4 border-b bg-slate-50/50 flex flex-row items-center justify-between">
                      <CardTitle className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                        <DollarSign className="w-3.5 h-3.5 text-slate-500" />
                        Situação de Crédito (Omie)
                      </CardTitle>
                      {dadosFinanceiros.situacaoCredito?.bloqueado && (
                        <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                          <Ban className="w-3 h-3 mr-1 text-rose-600" />
                          Bloqueado (Omie)
                        </Badge>
                      )}
                    </CardHeader>
                    <CardContent className="p-4 space-y-2 text-xs">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className="text-slate-400 block text-[11px]">Limite de Crédito</span>
                          <span className="font-mono font-semibold text-slate-900 text-sm">
                            {formatarMoeda(dadosFinanceiros.situacaoCredito?.limiteCredito)}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[11px]">Status de Crédito</span>
                          <span className="font-medium text-slate-800">
                            {dadosFinanceiros.situacaoCredito?.bloqueado
                              ? 'Bloqueado para faturamento'
                              : dadosFinanceiros.situacaoCredito?.inadimplente
                              ? 'Inadimplente'
                              : 'Regular'}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* 3c: Contas a Receber na Omie */}
                  <Card className="bg-white border-slate-200">
                    <CardHeader className="py-3 px-4 border-b bg-slate-50/50 flex flex-row items-center justify-between">
                      <CardTitle className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5 text-slate-500" />
                        Contas a Receber (Omie)
                      </CardTitle>
                      {Number(dadosFinanceiros.contasReceber?.totalVencido || 0) > 0 && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                          <AlertTriangle className="w-3 h-3 mr-1 text-amber-600" />
                          Inadimplente
                        </Badge>
                      )}
                    </CardHeader>
                    <CardContent className="p-4 space-y-3 text-xs">
                      <div className="grid grid-cols-2 gap-3 pb-2 border-b">
                        <div>
                          <span className="text-slate-400 block text-[11px]">Total Saldo Devedor</span>
                          <span className="font-mono font-medium text-slate-800">
                            {formatarMoeda(dadosFinanceiros.contasReceber?.totalSaldoDevedor)}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[11px]">Total Vencido</span>
                          <span className={`font-mono font-semibold ${Number(dadosFinanceiros.contasReceber?.totalVencido || 0) > 0 ? 'text-amber-700' : 'text-slate-800'}`}>
                            {formatarMoeda(dadosFinanceiros.contasReceber?.totalVencido)}
                          </span>
                        </div>
                      </div>

                      <div>
                        <span className="text-slate-500 font-semibold block mb-2 text-[11px]">
                          Títulos em Aberto
                        </span>
                        {!dadosFinanceiros.contasReceber?.titulosEmAberto || dadosFinanceiros.contasReceber.titulosEmAberto.length === 0 ? (
                          <p className="text-slate-400 text-[11px] italic">Nenhum título em aberto.</p>
                        ) : (
                          <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                            {dadosFinanceiros.contasReceber.titulosEmAberto.map((tit, idx) => (
                              <div
                                key={tit.numeroTitulo || idx}
                                className="flex items-center justify-between p-2 rounded bg-slate-50 border text-[11px]"
                              >
                                <div>
                                  <span className="font-mono font-medium text-slate-800 block">
                                    {tit.numeroTitulo || 'Título sem nº'}
                                  </span>
                                  <span className="text-slate-400 text-[10px]">
                                    Venc: {tit.dataVencimento || '—'}
                                  </span>
                                </div>
                                <div className="text-right">
                                  <span className="font-mono font-semibold text-slate-900 block">
                                    {formatarMoeda(tit.valor)}
                                  </span>
                                  {tit.status === 'VENCIDO' ? (
                                    <span className="text-[10px] font-bold text-amber-700">
                                      Vencido ({tit.diasAtraso}d)
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-slate-500">A vencer</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* 3a: Nossos Faturamentos (Últimos Retornos) */}
                  <Card className="bg-white border-slate-200">
                    <CardHeader className="py-3 px-4 border-b bg-slate-50/50">
                      <CardTitle className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5 text-slate-500" />
                        Histórico de Faturamento Interno
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 text-xs">
                      {!dadosFinanceiros.nossoFaturamento || dadosFinanceiros.nossoFaturamento.length === 0 ? (
                        <p className="text-slate-400 text-[11px] italic">Nenhum faturamento registrado.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {dadosFinanceiros.nossoFaturamento.map((fat) => (
                            <div
                              key={fat.retornoId}
                              className="flex items-center justify-between p-2 rounded bg-slate-50 border text-[11px]"
                            >
                              <div>
                                <span className="font-mono font-medium text-slate-800 block">
                                  Retorno {fat.retornoId.slice(0, 8)}
                                </span>
                                <span className="text-slate-400 text-[10px]">
                                  {fat.dataEmissao ? new Date(fat.dataEmissao).toLocaleDateString('pt-BR') : 'Data n/d'}
                                </span>
                              </div>
                              <div className="text-right">
                                <span
                                  className={
                                    fat.valorTotal != null
                                      ? 'font-mono font-medium text-slate-800 block'
                                      : 'font-mono text-slate-400 italic block text-[10px]'
                                  }
                                >
                                  {/* CR-04: valorTotal só existe quando o valor real do título/NF
                                      emitido na Omie estiver disponível — nunca exibir um número
                                      fabricado como se fosse faturamento real. */}
                                  {fat.valorTotal != null ? formatarMoeda(fat.valorTotal) : 'Valor não disponível'}
                                </span>
                                <Badge variant="outline" className="text-[9px] px-1.5 py-0 uppercase">
                                  {fat.status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ) : null}
            </div>
          )}
        </ScrollArea>
      </FormDialogContent>
    </FormDialog>
  );
}
